import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit, Trash2, Package, Filter, 
  Sun, Zap, Battery, Car, Wrench, Settings2, Copy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { installationProductAPI } from '../lib/api';
import { toast } from 'sonner';
import { DatasheetsManager } from '../components/DatasheetsManager';

const CATEGORIES = [
  { value: 'zonnepanelen', label: 'Zonnepanelen', icon: Sun, color: 'bg-amber-500' },
  { value: 'omvormers', label: 'Omvormers', icon: Zap, color: 'bg-blue-500' },
  { value: 'batterijen', label: 'Batterijen', icon: Battery, color: 'bg-green-500' },
  { value: 'laadpalen', label: 'Laadpalen', icon: Car, color: 'bg-purple-500' },
  { value: 'montagemateriaal', label: 'Montagemateriaal', icon: Settings2, color: 'bg-gray-500' },
  { value: 'werken', label: 'Werken', icon: Wrench, color: 'bg-orange-500' },
  { value: 'elektriciteitswerken', label: 'Elektriciteitswerken', icon: Zap, color: 'bg-yellow-500' },
];

const UNITS = [
  { value: 'stuk', label: 'Stuk' },
  { value: 'set', label: 'Set' },
  { value: 'm2', label: 'm²' },
  { value: 'forfait', label: 'Forfait' },
  { value: 'uur', label: 'Uur' },
];

const Producten = () => {
  const [products, setProducts] = useState([]);
  const [datasheetsOpen, setDatasheetsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seedingElektriciteit, setSeedingElektriciteit] = useState(false);
  
  const defaultFormData = {
    name: '',
    category: 'zonnepanelen',
    description: '',
    components: [],  // Array of { description: '', quantity: 1 } - spec lines for PDF
    sub_products: [], // Array of { product_id, name, unit_price, default_quantity } - real sub-products
    brand: '',
    model: '',
    specifications: '',
    warranty_years: null,
    warranty_description: '',
    unit_price: 0,
    unit: 'stuk',
    is_active: true,
  };
  
  const [formData, setFormData] = useState(defaultFormData);
  const [subProductSearch, setSubProductSearch] = useState('');
  const [showSubProductDropdown, setShowSubProductDropdown] = useState(false);
  
  useEffect(() => {
    fetchProducts();
  }, []);
  
  const fetchProducts = async () => {
    try {
      const response = await installationProductAPI.getAll({ active_only: false });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Kon producten niet laden');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        category: product.category || 'zonnepanelen',
        description: product.description || '',
        components: product.components || [],
        sub_products: product.sub_products || [],
        brand: product.brand || '',
        model: product.model || '',
        specifications: product.specifications || '',
        warranty_years: product.warranty_years || null,
        warranty_description: product.warranty_description || '',
        unit_price: product.unit_price || 0,
        unit: product.unit || 'stuk',
        is_active: product.is_active !== false,
      });
    } else {
      setEditingProduct(null);
      setFormData(defaultFormData);
    }
    setSubProductSearch('');
    setShowSubProductDropdown(false);
    setDialogOpen(true);
  };
  
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Productnaam is verplicht');
      return;
    }
    
    setSaving(true);
    try {
      if (editingProduct) {
        await installationProductAPI.update(editingProduct.id, formData);
        toast.success('Product bijgewerkt');
      } else {
        await installationProductAPI.create(formData);
        toast.success('Product aangemaakt');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Kon product niet opslaan');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (productId) => {
    try {
      await installationProductAPI.delete(productId);
      toast.success('Product verwijderd');
      setDeleteConfirm(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Kon product niet verwijderen');
    }
  };

  // Import Elektriciteitswerken producten
  const handleSeedElektriciteit = async () => {
    setSeedingElektriciteit(true);
    try {
      const response = await installationProductAPI.seedElektriciteit();
      const data = response.data;
      if (data.success) {
        toast.success(`${data.added_count} producten toegevoegd, ${data.skipped_count} overgeslagen`);
        fetchProducts();
      } else {
        toast.error('Fout bij importeren');
      }
    } catch (error) {
      console.error('Error seeding elektriciteit:', error);
      toast.error('Kon elektriciteitswerken niet importeren');
    } finally {
      setSeedingElektriciteit(false);
    }
  };

  const handleDuplicate = (product) => {
    // Open dialog with product data but clear the editing state (creates new)
    setEditingProduct(null);
    setFormData({
      name: `${product.name} (kopie)`,
      category: product.category || 'zonnepanelen',
      description: product.description || '',
      components: product.components ? [...product.components] : [],
      sub_products: product.sub_products ? [...product.sub_products] : [],
      brand: product.brand || '',
      model: product.model || '',
      specifications: product.specifications || '',
      warranty_years: product.warranty_years || null,
      warranty_description: product.warranty_description || '',
      unit_price: product.unit_price || 0,
      unit: product.unit || 'stuk',
      is_active: true,
    });
    setSubProductSearch('');
    setShowSubProductDropdown(false);
    setDialogOpen(true);
    toast.info('Product gedupliceerd - pas aan en sla op');
  };
  
  const filteredProducts = products.filter(product => {
    const matchesSearch = !search || 
      product.name?.toLowerCase().includes(search.toLowerCase()) ||
      product.brand?.toLowerCase().includes(search.toLowerCase()) ||
      product.model?.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = activeCategory === 'all' || product.category === activeCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  const getCategoryInfo = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-BE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };
  
  const productsByCategory = CATEGORIES.map(cat => ({
    ...cat,
    count: products.filter(p => p.category === cat.value && p.is_active).length,
  }));

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Producten</h1>
          <p className="text-muted-foreground">
            Beheer je productcatalogus voor installatie offertes
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setDatasheetsOpen(true)} data-testid="datasheets-btn">
            <Package className="h-4 w-4 mr-2" />
            Datasheets
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nieuw Product
          </Button>
        </div>
      </div>
      
      <DatasheetsManager open={datasheetsOpen} onOpenChange={setDatasheetsOpen} products={products} />
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {productsByCategory.map(cat => {
          const Icon = cat.icon;
          return (
            <Card 
              key={cat.value}
              className={`cursor-pointer transition-all hover:shadow-md ${activeCategory === cat.value ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setActiveCategory(activeCategory === cat.value ? 'all' : cat.value)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${cat.color}`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{cat.label}</p>
                    <p className="text-xl font-bold">{cat.count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op naam, merk of model..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="product-search-input"
          />
        </div>
        {activeCategory !== 'all' && (
          <Button variant="outline" size="sm" onClick={() => setActiveCategory('all')}>
            <Filter className="h-4 w-4 mr-2" />
            Filter wissen
          </Button>
        )}
      </div>
      
      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Geen producten gevonden</h3>
            <p className="text-muted-foreground text-center max-w-sm mt-2">
              {search || activeCategory !== 'all' 
                ? 'Probeer een andere zoekopdracht of filter'
                : 'Voeg je eerste product toe om te beginnen'}
            </p>
            {!search && activeCategory === 'all' && (
              <Button className="mt-4" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Eerste Product Toevoegen
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map(product => {
            const catInfo = getCategoryInfo(product.category);
            const Icon = catInfo.icon;
            
            return (
              <Card 
                key={product.id} 
                className={`hover:shadow-md transition-shadow ${!product.is_active ? 'opacity-60' : ''}`}
                data-testid={`product-card-${product.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded ${catInfo.color}`}>
                        <Icon className="h-3.5 w-3.5 text-white" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {catInfo.label}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDuplicate(product)}
                        title="Dupliceren"
                        data-testid={`duplicate-product-${product.id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleOpenDialog(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDeleteConfirm(product)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-lg mb-1">{product.name}</h3>
                  
                  {(product.brand || product.model) && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {[product.brand, product.model].filter(Boolean).join(' - ')}
                    </p>
                  )}
                  
                  {product.specifications && typeof product.specifications === 'string' && (
                    <p className="text-sm text-blue-600 mb-2">{product.specifications}</p>
                  )}
                  
                  {product.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {product.description}
                    </p>
                  )}
                  
                  {/* Show components if present */}
                  {product.components && product.components.length > 0 && (
                    <div className="text-sm text-muted-foreground mb-3 space-y-1">
                      {product.components.map((comp, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="line-clamp-1">{comp.description}</span>
                          <span className="text-xs text-primary ml-2">x{comp.quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show sub-products if present */}
                  {product.sub_products && product.sub_products.length > 0 && (
                    <div className="text-sm mb-3 space-y-1 border-l-2 border-primary/30 pl-2">
                      <p className="text-xs text-muted-foreground font-medium">Sub-producten:</p>
                      {product.sub_products.map((sp, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="line-clamp-1">{sp.name}</span>
                          <span className="text-primary ml-2">x{sp.default_quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div>
                      <p className="text-xl font-bold text-primary">
                        {formatPrice(product.unit_price)}
                      </p>
                      <p className="text-xs text-muted-foreground">per {product.unit}</p>
                    </div>
                    {product.warranty_years && (
                      <Badge variant="secondary" className="text-xs">
                        {product.warranty_years} jaar garantie
                      </Badge>
                    )}
                  </div>
                  
                  {!product.is_active && (
                    <Badge variant="outline" className="mt-2 text-orange-600 border-orange-300">
                      Inactief
                    </Badge>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Product Bewerken' : 'Nieuw Product'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Productnaam *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="bijv. Zonnepaneel DMEGC 465Wp"
                  data-testid="product-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Categorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="brand">Merk</Label>
                <Input
                  id="brand"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="bijv. DMEGC, SAJ, Solarwatt"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="bijv. DM465G12RT-G48HBB-L"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="specifications">Specificaties</Label>
              <Input
                id="specifications"
                value={formData.specifications}
                onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                placeholder="bijv. 465 Wp, 5 kW, 10.2 kWh"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Uitgebreide productomschrijving..."
                rows={3}
              />
            </div>
            
            {/* Components Section - Multiple descriptions with quantities */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Specificatie Regels (voor offerte)</Label>
                  <p className="text-xs text-muted-foreground">Deze regels worden onder het product getoond in de offerte PDF</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({
                    ...formData,
                    components: [...(formData.components || []), { description: '', quantity: 1 }]
                  })}
                >
                  <Plus className="w-4 h-4 mr-1" /> Regel toevoegen
                </Button>
              </div>
              
              {formData.components && formData.components.length > 0 && (
                <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                  {formData.components.map((component, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">-</span>
                      <Input
                        className="flex-1"
                        value={component.description}
                        onChange={(e) => {
                          const newComponents = [...formData.components];
                          newComponents[index].description = e.target.value;
                          setFormData({ ...formData, components: newComponents });
                        }}
                        placeholder="bijv. 25 jaar productgarantie + 30 jaar vermogensgarantie"
                      />
                      <Input
                        className="w-20"
                        type="number"
                        min="0"
                        value={component.quantity ?? ''}
                        onChange={(e) => {
                          const newComponents = [...formData.components];
                          newComponents[index].quantity = e.target.value === '' ? 0 : parseInt(e.target.value);
                          setFormData({ ...formData, components: newComponents });
                        }}
                        placeholder="Aantal"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newComponents = formData.components.filter((_, i) => i !== index);
                          setFormData({ ...formData, components: newComponents });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Sub-producten sectie */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Sub-producten
                  </Label>
                  <p className="text-xs text-muted-foreground">Selecteer producten die standaard bij dit product horen (bijv. kabeling bij batterij)</p>
                </div>
              </div>
              
              {/* Sub-product search */}
              <div className="relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Zoek product om toe te voegen..."
                      value={subProductSearch}
                      onChange={(e) => {
                        setSubProductSearch(e.target.value);
                        setShowSubProductDropdown(true);
                      }}
                      onFocus={() => setShowSubProductDropdown(true)}
                      data-testid="sub-product-search"
                    />
                  </div>
                </div>
                
                {/* Dropdown met zoekresultaten */}
                {showSubProductDropdown && subProductSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {products
                      .filter(p => 
                        p.id !== editingProduct?.id && // Niet zichzelf
                        !formData.sub_products?.some(sp => sp.product_id === p.id) && // Nog niet toegevoegd
                        (p.name.toLowerCase().includes(subProductSearch.toLowerCase()) ||
                         p.category.toLowerCase().includes(subProductSearch.toLowerCase()))
                      )
                      .slice(0, 8)
                      .map(product => (
                        <div
                          key={product.id}
                          className="px-3 py-2 hover:bg-muted cursor-pointer flex items-center justify-between"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              sub_products: [
                                ...(formData.sub_products || []),
                                {
                                  product_id: product.id,
                                  name: product.name,
                                  unit_price: product.unit_price,
                                  unit: product.unit,
                                  default_quantity: 1
                                }
                              ]
                            });
                            setSubProductSearch('');
                            setShowSubProductDropdown(false);
                          }}
                        >
                          <div>
                            <p className="font-medium text-sm">{product.name}</p>
                            <p className="text-xs text-muted-foreground">{CATEGORIES.find(c => c.value === product.category)?.label}</p>
                          </div>
                          <span className="text-sm text-primary">€{product.unit_price?.toFixed(2)}</span>
                        </div>
                      ))}
                    {products.filter(p => 
                      p.id !== editingProduct?.id &&
                      !formData.sub_products?.some(sp => sp.product_id === p.id) &&
                      (p.name.toLowerCase().includes(subProductSearch.toLowerCase()) ||
                       p.category.toLowerCase().includes(subProductSearch.toLowerCase()))
                    ).length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        Geen producten gevonden
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Toegevoegde sub-producten */}
              {formData.sub_products && formData.sub_products.length > 0 && (
                <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                  {formData.sub_products.map((subProduct, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1 text-sm">{subProduct.name}</span>
                      <span className="text-xs text-muted-foreground">€{subProduct.unit_price?.toFixed(2)}/{subProduct.unit}</span>
                      <Input
                        className="w-20"
                        type="number"
                        min="1"
                        value={subProduct.default_quantity}
                        onChange={(e) => {
                          const newSubProducts = [...formData.sub_products];
                          newSubProducts[index].default_quantity = parseInt(e.target.value) || 1;
                          setFormData({ ...formData, sub_products: newSubProducts });
                        }}
                        placeholder="Aantal"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newSubProducts = formData.sub_products.filter((_, i) => i !== index);
                          setFormData({ ...formData, sub_products: newSubProducts });
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="unit_price">Eenheidsprijs (€)</Label>
                <Input
                  id="unit_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                  data-testid="product-price-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="unit">Eenheid</Label>
                <Select 
                  value={formData.unit} 
                  onValueChange={(value) => setFormData({ ...formData, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(unit => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="warranty_years">Garantie (jaren)</Label>
                <Input
                  id="warranty_years"
                  type="number"
                  min="0"
                  value={formData.warranty_years || ''}
                  onChange={(e) => setFormData({ ...formData, warranty_years: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="warranty_description">Garantie beschrijving</Label>
              <Input
                id="warranty_description"
                value={formData.warranty_description}
                onChange={(e) => setFormData({ ...formData, warranty_description: e.target.value })}
                placeholder="bijv. 25 jaar productgarantie + 30 jaar vermogensgarantie op 87,4%"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-product-btn">
              {saving ? 'Opslaan...' : (editingProduct ? 'Bijwerken' : 'Aanmaken')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Product verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{deleteConfirm?.name}" wilt verwijderen? 
              Het product wordt als inactief gemarkeerd en niet meer getoond in offertes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteConfirm?.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Producten;
